import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Landmark, Plus, ChevronRight } from 'lucide-react'
import { useTenants } from '@/hooks/useTenants'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Badge } from '@/components/ui/badge'
import { BILLING_STATE_LABELS, billingDateLabel, billingDeadline, dollars } from '@/lib/billing'
import type { BillingSummaryDto } from '@lib/dto/billing-summary.dto'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'
import { LoginToTenantButton } from '@/components/LoginToTenantButton'
import { requestErrorMessage } from '@/helpers/apiClient'
import type { TenantListDto } from '@lib/dto/tenant.dto'

/** Platform tenant administration (super admin only): a calm list; creating and
 * renaming happen on their own pages. Super admins can log in to a tenant to
 * work inside it with org-admin powers. */
/** A company's billing at a glance: its state, what it brings in, and its next deadline. */
function TenantBillingSummary({ billing }: { billing: BillingSummaryDto }) {
  const deadline = billingDeadline(billing)
  return (
    <span className="hidden items-center gap-2 text-sm text-ink-muted lg:inline-flex">
      <Badge tone={billing.state === 'locked' || billing.state === 'past_due' ? 'warning' : 'neutral'}>
        {BILLING_STATE_LABELS[billing.state]}
      </Badge>
      <span>{dollars(billing.monthlyCents)}/mo</span>
      {deadline ? (
        <span>
          {deadline.label} {billingDateLabel(deadline.at)}
        </span>
      ) : null}
    </span>
  )
}

export function TenantsPage() {
  const navigate = useNavigate()
  const [page, setPage] = React.useState(1)
  const tenantsQuery = useTenants(page)

  function renderList() {
    if (tenantsQuery.isPending) return <LoadingState label="Loading tenants…" />
    if (tenantsQuery.isError) {
      return <ErrorState message={requestErrorMessage(tenantsQuery.error)} onRetry={() => void tenantsQuery.refetch()} />
    }
    const list: TenantListDto = tenantsQuery.data
    // `total`, not the row count: an out-of-range page comes back with no rows,
    // and keying the empty state off that would tell somebody their catalog was
    // empty when it is only this page that is.
    if (list.total === 0) {
      return (
        <EmptyState
          icon={Landmark}
          title="No tenants yet"
          description="Add your first organization to the platform."
          action={
            <Button onClick={() => navigate('/tenants/new')}>
              <Plus className="h-4 w-4" /> New tenant
            </Button>
          }
        />
      )
    }
    // The rows are the page the API returned; `total` is the whole list.
    const shown = serverPage(page, list.total, list.tenants.length)
    return (
      <div className="overflow-hidden rounded-md border border-line">
        <ul className="divide-y divide-line">
        {list.tenants.map((tenant) => (
          <li key={tenant.id} className="flex items-center gap-2 px-2 py-1.5 sm:pr-3">
            <button
              type="button"
              onClick={() => navigate(`/tenants/${tenant.id}`)}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-surface-hover"
            >
              <Landmark className="h-4 w-4 shrink-0 text-ink-muted" />
              <span className="min-w-0 flex-1 truncate font-medium">{tenant.name}</span>
              {/* Billing can't go live while any company lacks an owner, so the
                  ones still missing one are marked where they can be found. */}
              {tenant.billingOwner === null ? <Badge tone="warning">No billing owner</Badge> : null}
              {tenant.billing ? <TenantBillingSummary billing={tenant.billing} /> : null}
              <span className="hidden text-sm text-ink-muted sm:inline">
                {tenant.venueCount} venues · {tenant.userCount} users · {tenant.eventCount} events
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
            </button>
            <LoginToTenantButton tenantId={tenant.id} size="sm" variant="default" className="shrink-0" />
          </li>
        ))}
        </ul>
        {/* Inside the box, like a table's footer: the bar belongs to the list. */}
        <Pagination
          page={shown.page}
          pageCount={shown.pageCount}
          from={shown.from}
          to={shown.to}
          total={shown.total}
          unit="tenants"
          onPage={setPage}
        />
      </div>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Tenants"
          description="Organizations on the platform — open one for settings, or log in to work inside it."
          meta={tenantsQuery.data ? `${tenantsQuery.data.total} total` : undefined}
        />
        <Button onClick={() => navigate('/tenants/new')}>
          <Plus className="h-4 w-4" /> New tenant
        </Button>
      </div>

      {renderList()}
    </PageContainer>
  )
}
